/**
 * `wip.*` wire contract (garment F0, `garment.wip`).
 *
 * The size-wise shop-floor bucket brigade + the per-style WIP read-model.
 * Quantities are plain integer pieces (`qty` is signed — negative = correction).
 * A `loss` must carry a reason (enforced here AND in the domain). Behind
 * `entitledProcedureFor("garment.production")`.
 */
import { z } from "zod";
import { uuid, isoDate } from "../_common.js";

export const wipStage = z.enum([
  "cutting",
  "stitching",
  "khaja",
  "washing",
  "ironing",
  "qc",
  "packing",
]);
export const wipKind = z.enum(["in", "out", "loss"]);
export const cutPieceKind = z.enum(["panel", "damaged", "balance_fabric", "accessory"]);
const pieces = z.number().int().min(-1_000_000).max(1_000_000);

export const getForStyleInput = z.object({ styleId: uuid });

export const recordInput = z
  .object({
    styleId: uuid,
    stage: wipStage,
    size: z.string().trim().min(1).max(16),
    kind: wipKind,
    qty: pieces,
    reason: z.string().trim().max(80).nullable().optional(),
    entryDate: isoDate,
    lineRef: z.string().trim().max(40).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .refine((v) => v.kind !== "loss" || (v.reason != null && v.reason.length > 0), {
    message: "A loss needs a reason.",
    path: ["reason"],
  });
export const recordOutput = z.object({ id: uuid, styleStatus: z.string() });

export const recordCutPieceInput = z.object({
  styleId: uuid,
  kind: cutPieceKind,
  qty: pieces,
  uom: z.string().trim().min(1).max(8),
  note: z.string().trim().max(120).nullable().optional(),
  entryDate: isoDate,
});
export const recordCutPieceOutput = z.object({ id: uuid });

const sizeBalance = z.object({
  size: z.string(),
  received: z.number().int(),
  output: z.number().int(),
  loss: z.number().int(),
  balance: z.number().int(),
});
const stageWip = z.object({
  stage: wipStage,
  label: z.string(),
  sizes: z.array(sizeBalance),
  received: z.number().int(),
  output: z.number().int(),
  loss: z.number().int(),
  balance: z.number().int(),
});
export const styleWipView = z.object({
  styleId: uuid,
  status: z.string(),
  orderQty: z.number().int().nonnegative(),
  sizes: z.array(z.string()),
  stages: z.array(stageWip),
  finishedGoods: z.array(z.object({ size: z.string(), qty: z.number().int() })),
  finishedTotal: z.number().int(),
  cutPieces: z.array(z.object({ kind: cutPieceKind, qty: z.number().int(), uom: z.string() })),
});
export const getForStyleOutput = styleWipView;

export const queueInput = z.object({ stage: wipStage });
export const stageQueueView = z.object({
  stage: wipStage,
  label: z.string(),
  rows: z.array(
    z.object({
      styleId: uuid,
      styleRef: z.string(),
      name: z.string(),
      buyerName: z.string(),
      exFactoryDate: isoDate.nullable(),
      received: z.number().int(),
      output: z.number().int(),
      loss: z.number().int(),
      balance: z.number().int(),
    }),
  ),
});
export const queueOutput = stageQueueView;

// Cutting reconciliation — fabric issued ÷ per-piece standard → expected vs actual.
export const cutPlanInput = z.object({ styleId: uuid });
const cutPlanFabricLine = z.object({
  bomLineId: uuid,
  label: z.string(),
  uom: z.string(),
  perPieceMilli: z.number().int().nonnegative(),
  wastageBp: z.number().int(),
  grossPerPieceMilli: z.number().int().nonnegative(),
  issuedMilli: z.number().int().nonnegative(),
  requiredMilli: z.number().int().nonnegative(),
  expectedPieces: z.number().int().nullable(),
  limiting: z.boolean(),
});
export const cutPlanView = z.object({
  styleId: uuid,
  orderQty: z.number().int().nonnegative(),
  hasFabricBom: z.boolean(),
  anyIssued: z.boolean(),
  actualCut: z.number().int(),
  expectedPieces: z.number().int().nullable(),
  variancePieces: z.number().int().nullable(),
  efficiencyBp: z.number().int().nullable(),
  fabricLines: z.array(cutPlanFabricLine),
});
export const cutPlanOutput = cutPlanView;

// Finished-goods board (order lens) — QC-passed output across producing orders.
const finishedGoodsRow = z.object({
  styleId: uuid,
  styleRef: z.string(),
  name: z.string(),
  buyerName: z.string(),
  exFactoryDate: isoDate.nullable(),
  status: z.string(),
  orderQty: z.number().int().nonnegative(),
  finished: z.number().int(),
  packed: z.number().int(),
  balanceToFinish: z.number().int(),
  complete: z.boolean(),
  dispatchStatus: z.enum(["none", "draft", "confirmed"]),
});
export const finishedGoodsView = z.object({
  rows: z.array(finishedGoodsRow),
  cutPieces: z.array(z.object({ kind: cutPieceKind, uom: z.string(), qty: z.number().int() })),
  totals: z.object({
    orderTotal: z.number().int(),
    finishedTotal: z.number().int(),
    packedTotal: z.number().int(),
    readyToDispatch: z.number().int(),
  }),
});
export const finishedGoodsOutput = finishedGoodsView;

// Stage-pivot control board (order × stage grid).
export const stageBoardView = z.object({
  stages: z.array(z.object({ stage: wipStage, label: z.string() })),
  rows: z.array(
    z.object({
      styleId: uuid,
      styleRef: z.string(),
      name: z.string(),
      buyerName: z.string(),
      exFactoryDate: isoDate.nullable(),
      cells: z.array(z.object({ stage: wipStage, output: z.number().int(), balance: z.number().int() })),
      inProgress: z.number().int(),
    }),
  ),
});
export const stageBoardOutput = stageBoardView;

export type WipStage = z.infer<typeof wipStage>;
export type StyleWipViewWire = z.infer<typeof styleWipView>;
export type CutPlanViewWire = z.infer<typeof cutPlanView>;
export type FinishedGoodsViewWire = z.infer<typeof finishedGoodsView>;
export type StageBoardViewWire = z.infer<typeof stageBoardView>;
